import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography, Button, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { CustomSentenceApi } from "../api/CustomSentenceApi";

interface AddSentenceDialogProps {
  open: boolean;
  sentence?: { text: string; id?: number } | null;
  onClose: () => void;
}

const AddSentenceDialog: React.FC<AddSentenceDialogProps> = ({
  open,
  sentence,
  onClose,
}) => {
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!sentence || !sentence.id) return;

    setIsLoading(true);
    try {
      const response = await CustomSentenceApi.createBySentenceId(sentence.id);
      if (response.status < 300) {
        setSuccessMessage("Sentence added successfully!");
      } else {
        throw new Error("Failed to add sentence: " + response.status);
      }
    } catch (err: any) {
      if (err && err.response && err.response.status === 490) {
        setSuccessMessage("Please validate your email before adding sentences.");
      } else if (err && err.response && err.response.status === 491) {
        const errorData = err.response.data;
        setSuccessMessage(
          typeof errorData === "string"
            ? errorData
            : errorData?.message ?? "An unexpected error occurred."
        );
      } else if (err && err.response && err.response.status === 492) {
        setSuccessMessage("This user is read only.");
      } else {
        setSuccessMessage(
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : "An unexpected error occurred."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSuccessMessage("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        <Typography variant="h4" sx={{ color: "#383838" }}>
          Confirmation
        </Typography>
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ color: "#8D8D8D" }}>
          Do you want to add this sentence to your Pronunciation list?
        </DialogContentText>

        <Typography variant="body1">{sentence && sentence.text}</Typography>

        {successMessage && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            {successMessage}
            <br />
            <Link component={RouterLink} to="/sentence">
              Go to your Pronunciation list
            </Link>
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", "& .MuiButton-root": { flex: 1 } }}>
        <Button onClick={handleClose} color="primary" sx={{ color: "#787878" }}>
          {successMessage ? "Close" : "Cancel"}
        </Button>

        {!successMessage && (
          <Button onClick={handleConfirm} color="primary" disabled={isLoading} sx={{ color: "#787878" }}>
            Confirm
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddSentenceDialog;
